import React, { createContext, useContext, useEffect, useState } from 'react'
import { authApi, rolesApi, User, UserPermission } from 'lib/api'

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<boolean>
  completeLogin: (userData: User) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
  updateUser: (user: User) => void
  isAuthenticated: boolean
  loading: boolean
  permissions: UserPermission[]
  hasPermission: (codename: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

function clearLegacyJwtStorage() {
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [permissions, setPermissions] = useState<UserPermission[]>([])

  const loadPermissions = async () => {
    try {
      const permsRes = await rolesApi.getUserPermissions()
      setPermissions(permsRes.data.permissions || [])
    } catch (e) {
      console.error('Failed to load permissions:', e)
      setPermissions([])
    }
  }

  const completeLogin = async (userData: User) => {
    clearLegacyJwtStorage()
    setUser(userData)
    localStorage.setItem('selektia_user', JSON.stringify(userData))
    await loadPermissions()
  }

  useEffect(() => {
    let cancelled = false

    const boot = async () => {
      const legacyRefresh = localStorage.getItem('refresh_token')
      if (legacyRefresh) {
        try {
          await authApi.refreshToken(legacyRefresh)
        } catch {
          // Cookie session will be established on next login
        }
      }
      clearLegacyJwtStorage()

      try {
        const response = await authApi.getProfile()
        if (cancelled) {
          return
        }
        setUser(response.data)
        localStorage.setItem('selektia_user', JSON.stringify(response.data))
        await loadPermissions()
      } catch (error) {
        if (!cancelled) {
          setUser(null)
          localStorage.removeItem('selektia_user')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    boot()
    return () => {
      cancelled = true
    }
  }, [])

  const login = async (email: string, password: string): Promise<boolean> => {
    setLoading(true)

    try {
      const response = await authApi.login(email, password)
      if (response.data.requires_2fa || !response.data.user) {
        setLoading(false)
        return false
      }
      await completeLogin(response.data.user)
      setLoading(false)
      return true
    } catch (error) {
      console.error('Login failed:', error)
      setLoading(false)
      return false
    }
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      setPermissions([])
      clearLegacyJwtStorage()
      localStorage.removeItem('selektia_user')
      localStorage.removeItem('selektia_email_connection')
      localStorage.removeItem('selektia_auto_sync_active')
      localStorage.removeItem('selektia_auto_sync_interval')
      localStorage.removeItem('selektia_auto_sync_pending')
      localStorage.removeItem('selektia_auto_sync_last')
      sessionStorage.removeItem('selektia_auto_sync_deleted')
    }
  }

  const refreshUser = async () => {
    try {
      const response = await authApi.getProfile()
      setUser(response.data)
      localStorage.setItem('selektia_user', JSON.stringify(response.data))
      await loadPermissions()
    } catch (error) {
      console.error('Failed to refresh user:', error)
    }
  }

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser)
    localStorage.setItem('selektia_user', JSON.stringify(updatedUser))
  }

  const value = {
    user,
    login,
    completeLogin,
    logout,
    refreshUser,
    updateUser,
    isAuthenticated: !!user,
    loading,
    permissions,
    hasPermission: (codename: string) => permissions.some(p => p.codename === codename)
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
